/**
 * **C08 · The Form 5089 worksheet** (Stream C, Documents.dc.html): the Concessionaire's Sales Tax
 * Return's figures, by area, for the finance director to file online. The rows are Money's
 * worksheet (`GET /money/reports/form-5089` — sales tax by area from the ledger and the items'
 * flags, tax at 6%); the festival's dates and the due date are Money's sales-tax filing card. This
 * lays them out and says in a word whether each area's sales are taxable.
 */
import { NO_DATA, type PaperCell, type PaperPage } from './paper_page.js';

type Cell = string | number | null;

export interface Form5089Input {
  /** The worksheet's rows: area, gross, taxable, not taxable, tax; null when Money did not answer. */
  rows: Cell[][] | null;
  totalTaxCents: number | null;
  /** The festival's days as Money's filing card says them (`Sep 10 – 12, 2026`). */
  festival: string | null;
  /** The due date in words (`Oct 23, 2026`). */
  due: string | null;
}

const num = (c: Cell | undefined): number | null => (typeof c === 'number' ? c : null);
/** Taxable in a word: all of it, none of it, or part. */
const taxable = (gross: number | null, tax: number | null): string =>
  gross === null || tax === null ? NO_DATA : tax === 0 ? 'no' : tax >= gross ? 'yes' : 'part';

export function form5089Page(i: Form5089Input): PaperPage {
  const rows: PaperCell[][] = (i.rows ?? []).map((r) => {
    const gross = num(r[1]);
    const tax = num(r[4]);
    return [
      String(r[0] ?? ''),
      gross ?? NO_DATA,
      taxable(gross, num(r[2])),
      tax === null ? NO_DATA : tax === 0 ? '—' : tax,
    ];
  });
  return {
    title: 'Form 5089 · worksheet',
    sub: `Concessionaire’s Sales Tax Return · Michigan Treasury · festival ${i.festival ?? NO_DATA} · due ${i.due ?? NO_DATA} (30 business days)`,
    blocks: [
      {
        head: 'TAXABLE SALES BY AREA',
        cols: '1fr 120px 100px 120px',
        rows: [
          ['', 'GROSS', 'TAXABLE?', 'TAX AT 6%'],
          ...(i.rows ? rows : [[NO_DATA, '', '', '']]),
          ['TOTAL TAX DUE', '', '', i.totalTaxCents ?? NO_DATA],
        ],
      },
      {
        head: '',
        cols: '1fr',
        rows: [
          [
            'Sales tax collected was included in the Square price. Figures are the Square pull plus cash counted, by area.',
          ],
        ],
      },
    ],
    sign: ['Prepared by · finance director', 'Reviewed by · director'],
    foot: '5089 worksheet · the Treasury form is filed online from these figures',
  };
}
